/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

/**
 * @typedef {import('App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/types').TestDataSummary} TestDataSummary
 * @typedef {import('App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/types').TestSummaryDetailedResults} TestSummaryDetailedResults
 */

/**
 * @typedef {object} SidePanelRef
 * @property {() => void} open
 * @property {() => void} close
 * @property {() => void} toggle
 * @property {boolean} isOpen
 */

import { SidePanel } from 'App/Pages/Project/IdpApplication/side-panel/SidePanel';
import { ViewSwitcher } from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/view-switcher';
import { ViewRenderer } from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/view-renderer';
import { TestSummaryTiles } from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/test-template/TestSummaryTiles';

import * as Styled from './TestResultSidePanel.styled';

/**
 * @param {object} props
 * @param {string} props.title
 * @param {React.RefObject<SidePanelRef>} props.sidePanelRef
 * @param {TestDataSummary['testSummaryResults']} props.testSummaryResults
 * @param {TestSummaryDetailedResults | null} props.selectedDocumentData
 * @param {'forms' | 'tables'} props.selectedView
 * @param {(view: { name: 'forms' | 'tables' }) => void} props.onViewChange
 * @param {import('App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/types').StructuredExtractionResults} props.templateData
 * @returns {JSX.Element}
 */
export const TestResultSidePanel = ({
  title,
  sidePanelRef,
  testSummaryResults,
  selectedDocumentData,
  selectedView,
  onViewChange,
  templateData
}) => {
  return (
    <SidePanel title={title} ref={sidePanelRef}>
      <Styled.SidePanelContent>
        <Styled.SidePanelHeader>Document extraction result</Styled.SidePanelHeader>
        <TestSummaryTiles testSummaryResults={testSummaryResults} />
        {selectedDocumentData && (
          <Styled.ViewSection>
            <ViewSwitcher selectedView={selectedView} onViewChange={onViewChange} />
            <ViewRenderer
              selectedView={selectedView}
              comparison={{
                extractedData: selectedDocumentData,
                templateData: templateData
              }}
            />
          </Styled.ViewSection>
        )}
      </Styled.SidePanelContent>
    </SidePanel>
  );
};
